/**
 * Fairfax & Favor remote-fragment renderer.
 *
 * Keeps fetch/parse/replace behaviour in one place for lazily rendered Liquid.
 * It deliberately does not execute scripts or observe the whole DOM.
 */
(() => {
  const parse = (markup) => new DOMParser().parseFromString(markup, 'text/html');

  const resolveTarget = (target) => {
    if (target instanceof Element) return target;
    if (typeof target === 'string') return document.querySelector(target);
    return null;
  };

  const extract = (markup, selector = '') => {
    const documentFragment = parse(markup);

    if (!selector) {
      return documentFragment.body.innerHTML;
    }

    const source = documentFragment.querySelector(selector);

    if (!source) {
      throw new Error(`The requested fragment (${selector}) was not returned.`);
    }

    return source.innerHTML;
  };

  const enhance = (root, context = {}) => {
    if (!root) return;

    window.dispatchEvent(
      new CustomEvent('faf:section-rendered', {
        detail: {
          root,
          ...context,
        },
      })
    );
  };

  const fetchText = async (url, { signal, headers = {} } = {}) => {
    const response = await fetch(url, {
      signal,
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'XMLHttpRequest',
        ...headers
      }
    });

    if (!response.ok) {
      throw new Error(`Content could not be loaded (${response.status}).`);
    }

    return response.text();
  };

  const render = async ({
    url,
    target,
    selector = '',
    signal,
    context = {}
  }) => {
    const destination = resolveTarget(target);

    if (!destination) {
      throw new Error('The destination for the remote content was not found.');
    }

    const markup = await fetchText(url, { signal });
    destination.innerHTML = extract(markup, selector);
    enhance(destination, { url, selector, ...context });

    return destination;
  };

  const applyCartSections = (payload, { openPanel = false } = {}) => {
    if (!payload) return;

    const cartStore = window.Alpine?.store?.('cart');
    const panelStore = window.Alpine?.store?.('lazyPanel');

    if (Array.isArray(payload.items) && cartStore?.setState) {
      cartStore.setState(payload);
    }

    const sections = payload.sections || {};

    if (sections['push-cart'] && panelStore?.setMarkup) {
      panelStore.setMarkup('push-cart', sections['push-cart']);
      panelStore.activePanel = 'push-cart';
      panelStore.status = 'ready';
      panelStore.error = '';

      if (openPanel) {
        panelStore.open = true;
      }
    }

    if (sections['cart-count']) {
      const count = document.querySelector('.js-cart-count');

      if (count) {
        count.innerHTML = sections['cart-count'];
        count.classList.remove('hidden');
        enhance(count, { sectionId: 'cart-count' });
      }
    }
  };

  window.FAFSections = {
    parse,
    extract,
    enhance,
    fetchText,
    render,
    applyCartSections
  };
})();
